// Phase 10 — Schema.org JSON-LD helpers.
//
// Every public page emits an Organization block (from
// buildOrganizationSchema) and a BreadcrumbList (from
// buildBreadcrumbListSchema). Specialised pages emit one of:
//   • SoftwareApplication (home)
//   • FAQPage            (/faq)
//   • Article            (/evidence)
//
// All helpers return a plain JS object ready for JSON.stringify inside
// a <script type="application/ld+json"> tag rendered by <SeoHead/>.
//
// Every Organization emission carries parentOrganization =
// The RLK Group with url=https://therlkgroup.com, per the
// cross-property authority-flow decision documented in R4.

import { PRODUCTION_DOMAIN_PLACEHOLDER, absoluteUrl } from "@/lib/seoPages";

const RLK_GROUP = {
    "@type": "Organization",
    name: "The RLK Group",
    url: "https://therlkgroup.com",
};

/** Organization schema — emitted on every public page. */
export function buildOrganizationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "UAVSimNet",
        legalName: "UAVSimNet (a product of The RLK Group)",
        url: `https://${PRODUCTION_DOMAIN_PLACEHOLDER}`,
        logo: `https://${PRODUCTION_DOMAIN_PLACEHOLDER}/favicon.svg`,
        description:
            "Counter-UAS simulation platform for defence procurement. High-fidelity interceptor flight dynamics, Monte Carlo engagement analysis, and evidence-grade reporting.",
        parentOrganization: RLK_GROUP,
        sameAs: ["https://therlkgroup.com"],
    };
}

/** Richer Organization block used on /about — adds founder, address. */
export function buildDetailedOrganizationSchema() {
    return {
        ...buildOrganizationSchema(),
        address: {
            "@type": "PostalAddress",
            streetAddress: "Unit 4 Strathclyde Business Centre",
            addressLocality: "Cambuslang",
            addressRegion: "South Lanarkshire",
            postalCode: "G72 7XR",
            addressCountry: "GB",
        },
        foundingLocation: {
            "@type": "Place",
            name: "Glasgow, Scotland",
        },
        areaServed: ["GB", "EU", "US", "CA", "AU", "NZ"],
    };
}

/** SoftwareApplication schema — emitted on the home page. */
export function buildSoftwareApplicationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "UAVSimNet",
        applicationCategory: "DefenseApplication",
        applicationSubCategory: "Counter-UAS Simulation",
        operatingSystem: "Web (multi-tenant SaaS)",
        url: `https://${PRODUCTION_DOMAIN_PLACEHOLDER}`,
        description:
            "High-fidelity counter-UAS simulation and mission analysis platform with validated interceptor models, JSBSim flight dynamics, and Monte Carlo engagement analysis.",
        publisher: RLK_GROUP,
        // Procurement rather than transactional — no price listed publicly.
        offers: {
            "@type": "Offer",
            category: "Enterprise subscription",
            availability: "https://schema.org/LimitedAvailability",
            url: `https://${PRODUCTION_DOMAIN_PLACEHOLDER}/contact`,
        },
    };
}

/** BreadcrumbList schema — derived from the current path.
 *
 *  Chain rules:
 *    /                           → [ Home ]
 *    /features                   → [ Home, Features ]
 *    /use-cases                  → [ Home, Use cases ]
 *    /plan/scenarios/:x/:y       → not emitted (not a marketing path)
 *
 *  For Phase 10 every public page sits one level below Home, so the
 *  chain is always 2 entries except the home page itself. */
export function buildBreadcrumbListSchema({ path, label }) {
    const clean = path === "/" ? "/" : path.replace(/\/+$/, "");
    const items = [
        {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: absoluteUrl("/"),
        },
    ];
    if (clean !== "/") {
        items.push({
            "@type": "ListItem",
            position: 2,
            name: label,
            item: absoluteUrl(clean),
        });
    }
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

/** FAQPage schema — emitted on /faq.
 *
 *  qaPairs: array of { question: string, answer: string (HTML-free) }. */
export function buildFaqPageSchema(qaPairs) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: qaPairs.map((p) => ({
            "@type": "Question",
            name: p.question,
            acceptedAnswer: {
                "@type": "Answer",
                text: p.answer,
            },
        })),
    };
}

/** Article schema — emitted on /evidence.
 *
 *  opts: { headline, description, datePublished (ISO), path }. */
export function buildArticleSchema({
    headline,
    description,
    datePublished,
    path,
}) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline,
        description,
        datePublished,
        dateModified: datePublished,
        author: RLK_GROUP,
        publisher: RLK_GROUP,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": absoluteUrl(path),
        },
        image: `https://${PRODUCTION_DOMAIN_PLACEHOLDER}/og-image.png`,
    };
}
